import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Importar traducciones
import es from './locales/es';
import en from './locales/en';
import pt from './locales/pt';

// Recursos de traducción
const resources = {
  es: { translation: es },
  en: { translation: en },
};

// Determinar idioma inicial priorizando el persist de Zustand
let initialLanguage = 'es';
if (typeof window !== 'undefined') {
  try {
    const persisted = localStorage.getItem('language-storage');
    if (persisted) {
      const parsed = JSON.parse(persisted);
      initialLanguage =
        parsed?.state?.currentLanguage || parsed?.currentLanguage || initialLanguage;
    } else {
      // Fallback a i18next cache si existe
      const cached = localStorage.getItem('i18nextLng');
      if (cached) initialLanguage = cached;
    }
  } catch (e) {
    // Ignorar errores de parseo y mantener 'es'
  }
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'es', // Idioma por defecto
    lng: initialLanguage, // Idioma inicial (persistente si existe)

    // Configuración del detector de idioma
    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },

    interpolation: {
      escapeValue: false, // React ya maneja XSS
    },

    // Configuración para desarrollo
    debug: process.env.NODE_ENV === 'development',

    // Configuración de namespace
    ns: 'translation',
    defaultNS: 'translation',
  });

/**
 * Instancia APARTE, fija en portugués, para los sitios de Brasil
 * (`forcedSiteLanguage(country) === 'pt'`, ver `@/lib/country`).
 *
 * Es otra instancia y no un `changeLanguage('pt')` de la global a propósito:
 * - en el servidor la instancia global es UNA para todas las requests (de todos
 *   los tenants): cambiarle el idioma por request haría que un sitio chileno
 *   renderizado al mismo tiempo saliera en portugués;
 * - así el HTML del servidor ya sale en portugués y el cliente hidrata igual
 *   (sin parpadeo español → portugués);
 * - ni el navegador, ni el idioma guardado de otra visita, ni el selector la
 *   pueden mover: no tiene detector y nadie le llama `changeLanguage`.
 *
 * Lo que falte en pt cae al español (mismo fallback que la global).
 *
 * OJO: SIN `.use(initReactI18next)`. Ese plugin registra la instancia como la
 * global de react-i18next; si se lo pusiéramos, cualquier `useTranslation` sin
 * provider —en cualquier sitio, chileno incluido— pasaría a hablar portugués.
 * Los sitios de Brasil la reciben solo por `<I18nextProvider>` (I18nProvider).
 */
export const i18nPt = i18n.createInstance();
i18nPt.init({
  resources: { es: { translation: es }, pt: { translation: pt } },
  lng: 'pt',
  fallbackLng: 'es',
  initAsync: false,
  interpolation: { escapeValue: false },
  // El logger de i18next es UNO por módulo: sin repetir esto, iniciar esta
  // instancia le apagaba el debug de desarrollo a la global.
  debug: process.env.NODE_ENV === 'development',
  ns: 'translation',
  defaultNS: 'translation',
});

/**
 * Instancia APARTE, fija en inglés, para los sitios de EE.UU.
 * (`forcedSiteLanguage(country) === 'en'`). Mismo diseño y mismos cuidados que
 * `i18nPt` (otra instancia, sin detector, sin `initReactI18next`, nadie le
 * llama `changeLanguage`): la global no cambia de idioma por esto, así que un
 * tenant chileno con default_language 'en' (Venuum, ERA) decide su idioma como
 * hoy.
 *
 * Los recursos 'en' son los mismos de la global (`./locales/en`, con todo lo que
 * se registre en `locales/en/index.ts`). Lo que falte en inglés cae al español,
 * igual que la global. Ojo: los namespaces nuevos en inglés (booking, catalog,
 * formsUi, site) también los ve el visitante de un sitio chileno que elige EN
 * (antes caían al español). Los correos y asuntos van en el idioma del TENANT
 * (`delTenant.ts`), así que eso no cambia lo que recibe la automotora.
 */
export const i18nEn = i18n.createInstance();
i18nEn.init({
  resources: { es: { translation: es }, en: { translation: en } },
  lng: 'en',
  fallbackLng: 'es',
  initAsync: false,
  interpolation: { escapeValue: false },
  debug: process.env.NODE_ENV === 'development',
  ns: 'translation',
  defaultNS: 'translation',
});

export default i18n;
